import React, { useState, useMemo, useRef } from "react";
import { useNavigate, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { audiencesApi } from "../../redux/api/audiencesApi";
import AsyncSelect from "react-select/async";
import { categoriesService } from "../../services/categories";
import { audiencesService } from "../../services/audiences";
import { PLATFORMS_ARRAY } from "../../utils/platforms";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { trackEvent } from "../../utils/tracking";
import Modal from "../../components/Modal";

function categorySelectLabel(category) {
  if (!category) return "";
  const base = (category.display_name || category.name || "").trim();
  const em = (category.emoji || "").trim();
  return em ? `${em} ${base}` : base;
}

const CreateAudience = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentProject } = useSelector((state) => state.project);
  const { currentBrand } = useSelector((state) => state.brand);
  const [selectedPlatform, setSelectedPlatform] = useState("instagram");
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [creationMode, setCreationMode] = useState("category");
  const [manualHandles, setManualHandles] = useState("");
  const [manualAudienceName, setManualAudienceName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const debounceTimerRef = useRef(null);
  
  // Check if we're on the populating route
  const isPopulating = location.pathname.includes('/populating/');

  const handlePlatformSelect = (platform) => {
    if (!platform.enabled) return;
    setSelectedPlatform(platform.id);
    setSelectedTopic(null);
    setAllLoadedTopics([]); // Reset when platform changes
    setTotalCategoryCount(null);
    setManualHandles("");
    setManualAudienceName("");
  };

  // Track all loaded topics to check if categories are the same
  const [allLoadedTopics, setAllLoadedTopics] = useState([]);
  // Total category count from first fetch (for placeholder)
  const [totalCategoryCount, setTotalCategoryCount] = useState(null);

  // Load options for react-select
  const loadOptions = (inputValue, callback) => {
    if (!selectedPlatform) {
      callback([]);
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the API call
    debounceTimerRef.current = setTimeout(() => {
      const search = inputValue.trim() || null;
      const params = {
        platform: selectedPlatform,
      };

      const mapToOptions = (categories) =>
        categories.map((category) => ({
          value: category.platform_id,
          label: categorySelectLabel(category),
          topic: category,
        }));

      if (search) {
        params.search = search;
        categoriesService.getCategories(
          (response) => {
            const categories = Array.isArray(response.data?.results)
              ? response.data.results
              : [];
            const options = mapToOptions(categories);
            callback(options);
          },
          () => {
            callback([]);
          },
          params
        );
        return;
      }

      params.num_results = 20;
      const popularPromise = new Promise((resolve) => {
        categoriesService.getCategories(
          (response) => {
            resolve({
              results: Array.isArray(response.data?.results)
                ? response.data.results
                : [],
              count: response.data?.count,
            });
          },
          () => resolve({ results: [], count: null }),
          params
        );
      });

      const suggestPromise =
        currentBrand?.hash
          ? new Promise((resolve) => {
              categoriesService.getCategorySuggestions(
                (response) => {
                  resolve(
                    Array.isArray(response.data?.results)
                      ? response.data.results
                      : []
                  );
                },
                () => resolve([]),
                {
                  platform: selectedPlatform,
                  brand_hash: currentBrand.hash,
                }
              );
            })
          : Promise.resolve([]);

      Promise.all([popularPromise, suggestPromise]).then(
        ([popularWrap, suggestResults]) => {
          const categories =
            suggestResults.length > 0 ? suggestResults : popularWrap.results;
          setAllLoadedTopics(categories);
          if (typeof popularWrap.count === 'number') {
            setTotalCategoryCount(popularWrap.count);
          }
          callback(mapToOptions(categories));
        }
      );
    }, 250);
  };

  // Check if all categories have the same parent_category
  const allSameCategory = useMemo(() => {
    if (!Array.isArray(allLoadedTopics) || allLoadedTopics.length === 0) return false;
    const parentCategories = allLoadedTopics.map(t => t.parent_category).filter(Boolean);
    return parentCategories.length > 0 && new Set(parentCategories).size === 1;
  }, [allLoadedTopics]);

  const handleCreate = () => {
    if (!selectedPlatform || !currentProject) return;
    if (creationMode === "category" && !selectedTopic) return;
    if (creationMode === "manual") {
      if (!manualAudienceName.trim()) {
        setError("Enter a name for this audience.");
        return;
      }
      if (!manualHandles.trim()) {
        setError("Paste at least one creator handle.");
        return;
      }
    }

    setCreating(true);
    setError(null);
    const payload =
      creationMode === "manual"
        ? {
            creation_mode: "manual",
            platform: selectedPlatform,
            handles: manualHandles,
            project_hash: currentProject.hash,
            name: manualAudienceName.trim(),
          }
        : {
            platform: selectedPlatform,
            platform_id: selectedTopic.platform_id,
            project_hash: currentProject.hash,
          };

    audiencesService.createAudience(
      payload,
      (response) => {
        setCreating(false);
        setError(null);
        const audience = response.data;
        trackEvent("create_audience", {
          platform: selectedPlatform,
          audience_hash: audience?.hash,
          project_hash: currentProject?.hash,
          creation_mode: creationMode,
        });
        if (currentProject?.hash) {
          dispatch(
            audiencesApi.util.invalidateTags([{ type: "Audiences", id: currentProject.hash }])
          );
        }
        navigate(`populating/${audience.hash}/`);
      },
      (err) => {
        setCreating(false);
        setError(getApiErrorMessage(err, "Failed to create audience. Please try again."));
      }
    );
  };

  const handleClose = () => {
    navigate("/research/");
  };

  return (
    <Modal onClose={handleClose}>
      {!isPopulating ? (
        <>
          <h2 className="text-2xl font-semibold text-theme-text mb-6">
            Create New Audience
          </h2>

          {error && (
            <div className="mb-6 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded text-sm text-theme-text">
              {error}
            </div>
          )}

          {/* Platform Selection */}
          <div className="mb-6">
        <label className="block text-sm font-medium text-theme-text mb-3">
          Select Platform
        </label>
        <div className="grid grid-cols-3 gap-4">
          {PLATFORMS_ARRAY.map((platform) => (
            <button
              key={platform.id}
              onClick={() => handlePlatformSelect(platform)}
              disabled={!platform.enabled}
              className={`relative p-4 rounded-lg border-2 transition-all text-center ${
                selectedPlatform === platform.id
                  ? ""
                  : "border-theme-secondary hover:border-opacity-50"
              } ${
                !platform.enabled
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              style={{
                borderColor: selectedPlatform === platform.id && platform.enabled
                  ? platform.color
                  : undefined,
              }}
            >
              {selectedPlatform === platform.id && (
                <div className="absolute top-2 right-2">
                  <svg
                    className="h-5 w-5"
                    style={{ color: platform.color }}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
              <div className="flex flex-col items-center gap-2">
                <div style={{ color: platform.color }}>
                  {React.cloneElement(platform.icon, { className: "w-8 h-8" })}
                </div>
                <div className="text-lg font-medium text-theme-text text-center">
                  {platform.name}
                </div>
              </div>
              {!platform.enabled && (
                <div className="text-xs text-theme-text opacity-60 mt-1">Coming soon</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Topic or manual handles */}
      {creationMode === "category" ? (
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <span className="block text-sm font-medium text-theme-text">Select Category</span>
          <button
            type="button"
            onClick={() => {
              setCreationMode("manual");
              setSelectedTopic(null);
              setManualAudienceName("");
              setError(null);
            }}
            className="text-sm text-theme-primary hover:opacity-80"
          >
            Add creators manually
          </button>
        </div>
        <AsyncSelect
          key={`${selectedPlatform}-${currentBrand?.hash || ''}`}
          isDisabled={!selectedPlatform}
          placeholder={
            selectedPlatform
              ? totalCategoryCount != null
                ? `Start typing to search ${totalCategoryCount.toLocaleString()} categories...`
                : "Start typing name..."
              : "Select a platform first"
          }
          loadOptions={loadOptions}
          defaultOptions
          cacheOptions={false} // Disable cache so platform changes trigger new fetch
          value={selectedTopic ? {
            value: selectedTopic.platform_id,
            label: categorySelectLabel(selectedTopic),
            topic: selectedTopic,
          } : null}
          onChange={(option) => {
            setSelectedTopic(option ? option.topic : null);
          }}
          formatOptionLabel={({ label, topic }, { context }) => {
            // Instagram: never show parent (redundant; first API page can make allSameCategory wrong after reload).
            // Other platforms: show parent in menu when categories mix parents (e.g. YouTube later).
            const showCategory =
              selectedPlatform !== 'instagram' &&
              topic.parent_category &&
              context === 'menu' &&
              !allSameCategory;
            
            return (
              <div className="flex items-center justify-between w-full" style={{ width: '100%', margin: 0, padding: 0 }}>
                <div className="flex-1 min-w-0" style={{ margin: 0, padding: 0, overflow: 'hidden' }}>
                  <div className="font-medium text-theme-text truncate" style={{ width: '100%' }}>{label}</div>
                  {showCategory && (
                    <div className="text-sm text-theme-secondary-text truncate" style={{ width: '100%' }}>{topic.parent_category}</div>
                  )}
                </div>
                <div className="ml-4 text-sm text-theme-secondary-text whitespace-nowrap" style={{ flexShrink: 0 }}>
                  {topic.video_count > 0
                    ? `${topic.video_count.toLocaleString()} videos`
                    : "--"}
                </div>
              </div>
            );
          }}
          styles={{
            control: (base, state) => ({
              ...base,
              backgroundColor: 'var(--theme-main-bg)',
              borderColor: 'var(--theme-secondary-bg)',
              minHeight: '42px',
              '&:hover': {
                borderColor: 'var(--theme-secondary-bg)',
              },
              boxShadow: state.isFocused ? '0 0 0 1px var(--theme-secondary-bg)' : 'none',
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: 'var(--theme-main-bg)',
              borderColor: 'var(--theme-secondary-bg)',
              zIndex: 60,
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isSelected
                ? 'var(--theme-secondary-bg)'
                : state.isFocused
                ? 'var(--theme-secondary-bg)'
                : 'transparent',
              color: 'var(--theme-text)',
              padding: '8px 12px',
              paddingLeft: '12px',
              paddingRight: '12px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'var(--theme-secondary-bg)',
                opacity: 0.5,
              },
            }),
            menuList: (base) => ({
              ...base,
              padding: 0,
            }),
            input: (base) => ({
              ...base,
              color: 'var(--theme-text)',
              margin: 0,
              padding: 0,
            }),
            placeholder: (base) => ({
              ...base,
              color: 'var(--theme-secondary-text)',
            }),
            singleValue: (base) => ({
              ...base,
              color: 'var(--theme-text)',
              margin: 0,
            }),
            valueContainer: (base) => ({
              ...base,
              padding: '2px 8px',
            }),
          }}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>
      ) : (
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <span className="block text-sm font-medium text-theme-text">Creator handles</span>
          <button
            type="button"
            onClick={() => {
              setCreationMode("category");
              setManualHandles("");
              setManualAudienceName("");
              setError(null);
            }}
            className="text-sm text-theme-primary hover:opacity-80"
          >
            Add entire category
          </button>
        </div>
        <textarea
          value={manualHandles}
          onChange={(e) => setManualHandles(e.target.value)}
          rows={6}
          placeholder="creator1, @creator2 (spaces or commas)"
          className="w-full rounded-md border border-theme-secondary bg-theme-main text-theme-text px-3 py-2 text-sm placeholder:text-theme-text placeholder:opacity-50 focus:outline-none focus:ring-1 focus:ring-theme-secondary mb-4"
        />
        <label className="block text-sm font-medium text-theme-text mb-2">
          Name This Audience
        </label>
        <input
          type="text"
          value={manualAudienceName}
          onChange={(e) => setManualAudienceName(e.target.value)}
          placeholder="e.g. Spring campaign creators"
          className="w-full rounded-md border border-theme-secondary bg-theme-main text-theme-text px-3 py-2 text-sm placeholder:text-theme-text placeholder:opacity-50 focus:outline-none focus:ring-1 focus:ring-theme-secondary"
        />
      </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          onClick={handleClose}
          className="px-4 py-2 border border-theme-secondary rounded-md text-theme-text hover:bg-theme-secondary hover:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={
            creating ||
            (creationMode === "category" && !selectedTopic) ||
            (creationMode === "manual" &&
              (!manualAudienceName.trim() || !manualHandles.trim()))
          }
          className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {creating ? "Creating..." : "Create Audience"}
        </button>
      </div>
        </>
      ) : (
        /* Nested routes for populating modal */
        <Outlet />
      )}
    </Modal>
  );
};

export default CreateAudience;

